import { Controller } from "@hotwired/stimulus";

// Generic two-list staging: checking rows and clicking Add/Remove moves
// <tr>s between the two tbodies and stages a hidden field set (formIdValue
// owns actual persistence — nothing saves until that form submits).
export default class extends Controller {
  static targets = [
    "currentTbody",
    "availableTbody",
    "currentCheckbox",
    "availableCheckbox",
    "currentSelectAll",
    "availableSelectAll",
    "currentSearchForm",
    "availableSearchForm",
    "addButton",
    "removeButton",
    "hiddenFields",
    "pendingSummary"
  ];

  static values = {
    fieldName: String, // hidden field name, e.g. "invoice[order_ids][]"
    formId: String, // id of the external <form> that owns persistence
    currentIds: Array, // full "current" id set — the tbody only renders one page
    addResolveUrl: { type: String, default: "" }, // select-all GET, returns { ids }
    removeResolveUrl: { type: String, default: "" }
  };

  connect() {
    this._onFrameLoad = () => this.#markStagedRows();
    this.#frames().forEach((frame) => {
      frame.addEventListener("turbo:frame-load", this._onFrameLoad);
    });
    this.originalIds = new Set(this.currentIdsValue.map(String));
    this.stagedIds = new Set(this.originalIds);
    this.#renderHiddenFields();
    this.#refreshButtons();
    this.#refreshSummary();
    this.#markStagedRows();
  }

  disconnect() {
    this.#frames().forEach((frame) => {
      frame.removeEventListener("turbo:frame-load", this._onFrameLoad);
    });
  }

  toggleCurrentSelectAll({ target }) {
    this.currentCheckboxTargets.forEach((cb) => {
      cb.checked = target.checked;
    });
    this.#refreshButtons();
  }

  toggleAvailableSelectAll({ target }) {
    this.availableCheckboxTargets.forEach((cb) => {
      cb.checked = target.checked;
    });
    this.#refreshButtons();
  }

  toggleCheckbox() {
    this.#refreshButtons();
  }

  currentCheckboxTargetConnected(checkbox) {
    if (this.hasCurrentSelectAllTarget && this.currentSelectAllTarget.checked) {
      checkbox.checked = true;
    }
    this.#refreshButtons();
  }

  currentCheckboxTargetDisconnected() {
    this.#refreshButtons();
  }

  availableCheckboxTargetConnected(checkbox) {
    if (
      this.hasAvailableSelectAllTarget &&
      this.availableSelectAllTarget.checked
    ) {
      checkbox.checked = true;
    }
    this.#refreshButtons();
  }

  availableCheckboxTargetDisconnected() {
    this.#refreshButtons();
  }

  async stageAdd() {
    const ids = await this.#idsToStage({
      url: this.addResolveUrlValue,
      searchForm: this.hasAvailableSearchFormTarget
        ? this.availableSearchFormTarget
        : null,
      checkboxTargets: this.availableCheckboxTargets,
      selectAll:
        this.hasAvailableSelectAllTarget &&
        this.availableSelectAllTarget.checked
    });
    this.#stage(ids, {
      toTbody: this.currentTbodyTarget,
      checkboxTargets: this.availableCheckboxTargets,
      add: true
    });
    this.#afterStagingChange();
  }

  async stageRemove() {
    const ids = await this.#idsToStage({
      url: this.removeResolveUrlValue,
      searchForm: this.hasCurrentSearchFormTarget
        ? this.currentSearchFormTarget
        : null,
      checkboxTargets: this.currentCheckboxTargets,
      selectAll:
        this.hasCurrentSelectAllTarget && this.currentSelectAllTarget.checked
    });
    this.#stage(ids, {
      toTbody: this.availableTbodyTarget,
      checkboxTargets: this.currentCheckboxTargets,
      add: false
    });
    this.#afterStagingChange();
  }

  // Re-marks rows after a frame reload (e.g. filtering) without rebuilding
  // stagedIds from what's rendered — a reload only ever shows one page.
  #markStagedRows() {
    [
      this.hasCurrentTbodyTarget && this.currentTbodyTarget,
      this.hasAvailableTbodyTarget && this.availableTbodyTarget
    ]
      .filter(Boolean)
      .forEach((tbody) => {
        const isCurrent = tbody === this.currentTbodyTarget;
        tbody.querySelectorAll("[data-transfer-id]").forEach((row) => {
          const staged =
            this.stagedIds.has(row.dataset.transferId) !== isCurrent;
          row.classList.toggle("data-table__row--staged", staged);
        });
      });
  }

  async #idsToStage({ url, searchForm, checkboxTargets, selectAll }) {
    if (!selectAll || !url) {
      return checkboxTargets.filter((cb) => cb.checked).map((cb) => cb.value);
    }

    const params = new URLSearchParams({ select_all: "true" });
    if (searchForm) {
      new FormData(searchForm).forEach((value, key) => {
        if (value) params.append(key, value);
      });
    }
    const response = await fetch(`${url}?${params}`, {
      headers: { Accept: "application/json" }
    });
    const data = await response.json();
    return data.ids.map(String);
  }

  #stage(ids, { toTbody, checkboxTargets, add }) {
    ids.forEach((id) => {
      const checkbox = checkboxTargets.find((cb) => cb.value === id);
      if (checkbox) {
        checkbox.checked = false;
        this.#moveRow(checkbox.closest("tr"), toTbody);
      }
      if (add) this.stagedIds.add(id);
      else this.stagedIds.delete(id);
    });
  }

  #moveRow(row, toTbody) {
    if (!row) return;
    toTbody.appendChild(row);
    const nowCurrent = toTbody === this.currentTbodyTarget;
    this.#retargetCheckbox(row, nowCurrent);
    const belongsHere =
      row.dataset.origin === (nowCurrent ? "current" : "available");
    row.classList.toggle("data-table__row--staged", !belongsHere);
  }

  // Otherwise a re-checked, moved row stays invisible to the other side's button.
  #retargetCheckbox(row, nowCurrent) {
    const checkbox = row.querySelector("input[type=checkbox]");
    if (!checkbox) return;
    checkbox.setAttribute(
      "data-form--transfer-list-target",
      nowCurrent ? "currentCheckbox" : "availableCheckbox"
    );
  }

  #afterStagingChange() {
    this.#renderHiddenFields();
    this.#refreshButtons();
    this.#refreshSummary();
    document
      .getElementById(this.formIdValue)
      ?.dispatchEvent(new Event("change"));
  }

  #renderHiddenFields() {
    if (!this.hasHiddenFieldsTarget) return;
    const inputs = [...this.stagedIds].map((id) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = this.fieldNameValue;
      input.value = id;
      input.setAttribute("form", this.formIdValue);
      return input;
    });
    this.hiddenFieldsTarget.replaceChildren(...inputs);
  }

  #refreshButtons() {
    if (this.hasAddButtonTarget) {
      const hasSelection =
        this.availableCheckboxTargets.some((cb) => cb.checked) ||
        (this.hasAvailableSelectAllTarget &&
          this.availableSelectAllTarget.checked);
      this.addButtonTarget.disabled = !hasSelection;
    }
    if (this.hasRemoveButtonTarget) {
      const hasSelection =
        this.currentCheckboxTargets.some((cb) => cb.checked) ||
        (this.hasCurrentSelectAllTarget && this.currentSelectAllTarget.checked);
      this.removeButtonTarget.disabled = !hasSelection;
    }
  }

  #refreshSummary() {
    if (!this.hasPendingSummaryTarget) return;
    const added = [...this.stagedIds].filter((id) => !this.originalIds.has(id));
    const removed = [...this.originalIds].filter(
      (id) => !this.stagedIds.has(id)
    );
    const changed = added.length + removed.length;
    this.pendingSummaryTarget.textContent =
      changed > 0
        ? this.pendingSummaryTarget.dataset.template.replace(
            "%{count}",
            changed
          )
        : "";
  }

  #frames() {
    const tbodies = [];
    if (this.hasCurrentTbodyTarget) tbodies.push(this.currentTbodyTarget);
    if (this.hasAvailableTbodyTarget) tbodies.push(this.availableTbodyTarget);

    const frames = new Set();
    tbodies.forEach((tbody) => {
      const frame = tbody.closest("turbo-frame");
      if (frame) frames.add(frame);
    });
    return [...frames];
  }
}
